import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { EASE, requestPilot, scrollToId } from "./Reveal";

const HERO_IMG =
  "https://images.unsplash.com/photo-1472803828399-39d4ac53c6e5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMGRhcmslMjBnZW9tZXRyaWMlMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzg3MTk4MjI4fDA&ixlib=rb-4.1.0&q=85";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-[0.1em] -mb-[0.1em]">
    <motion.span
      className="block will-change-transform"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 20 });
  const sy = useSpring(my, { stiffness: 60, damping: 20 });

  const onMouseMove = (e) => {
    const { innerWidth, innerHeight } = window;
    mx.set((e.clientX / innerWidth - 0.5) * 24);
    my.set((e.clientY / innerHeight - 0.5) * 24);
  };

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      onMouseMove={onMouseMove}
      className="relative flex min-h-screen flex-col justify-end overflow-hidden"
    >
      <motion.div style={{ y: imgY }} className="absolute inset-0" aria-hidden>
        <img
          src={HERO_IMG}
          alt=""
          className="h-[125%] w-full object-cover opacity-35"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/80 via-ink/55 to-ink" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/80 via-transparent to-ink/40" />
        <div className="absolute inset-0 bg-grid opacity-70" />
      </motion.div>

      <div className="pointer-events-none absolute -top-32 right-[-10%] h-[480px] w-[480px] rounded-full bg-blue-500/10 blur-3xl" aria-hidden />

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-16 pt-32 sm:pb-24 sm:pt-40 lg:px-8">
        <motion.p
          data-testid="hero-overline"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.3 }}
          className="mb-8 font-mono text-[11px] uppercase leading-relaxed tracking-[0.18em] text-gray-400 sm:text-xs sm:tracking-[0.3em]"
        >
          Dignifyd Group — London · Dubai · Singapore · Toronto · Chicago · Delhi NCR
        </motion.p>

        <h1
          data-testid="hero-headline"
          className="font-heading text-5xl font-bold leading-[1.02] tracking-tighter sm:text-7xl lg:text-[6.5rem]"
        >
          <MaskedLine delay={0.45}>Tech, talent,</MaskedLine>
          <MaskedLine delay={0.6}>digital &amp; data —</MaskedLine>
          <MaskedLine delay={0.75}>
            <span className="text-gold">one partner.</span>
          </MaskedLine>
        </h1>

        <div className="mt-10 flex flex-col gap-10 lg:flex-row lg:items-end lg:justify-between">
          <motion.p
            data-testid="hero-subcopy"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05, ease: EASE }}
            className="max-w-xl text-base leading-relaxed text-gray-300 lg:text-lg"
          >
            We build your technology, hire your teams, run your infrastructure,
            grow your brand and turn your data into decisions — five specialist
            companies under one contract, across 35+ countries, 24/5.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.2, ease: EASE }}
            className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center sm:gap-4"
          >
            <button
              data-testid="hero-cta-conversation"
              onClick={() => scrollToId("#contact")}
              className="group flex w-full items-center justify-center gap-2 bg-gold px-7 py-4 sm:w-auto font-mono text-xs font-medium uppercase tracking-[0.15em] text-ink transition-colors duration-300 hover:bg-blue-300"
            >
              Start a conversation
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <button
              data-testid="hero-cta-explore"
              onClick={() => scrollToId("#entities")}
              className="group flex w-full items-center justify-center gap-2 border border-white/25 px-7 py-4 sm:w-auto font-mono text-xs font-medium uppercase tracking-[0.15em] text-white transition-colors duration-300 hover:border-gold hover:text-gold"
            >
              Explore the five entities
            </button>
          </motion.div>
        </div>

        <motion.div
          data-testid="hero-proof-strip"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.5 }}
          className="mt-16 flex flex-wrap gap-x-10 gap-y-3 border-t border-white/10 pt-6 font-mono text-xs uppercase tracking-[0.2em] text-gray-400"
        >
          <span>1,500+ projects delivered</span>
          <span>98% client satisfaction</span>
          <span>120+ group specialists</span>
          <span>Est. 2018</span>
        </motion.div>
      </motion.div>

      <motion.div
        data-testid="hero-floating-card"
        style={{ x: sx, y: sy }}
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 1.6, ease: EASE }}
        className="absolute right-10 top-32 z-10 hidden border border-white/10 bg-surface/70 p-6 backdrop-blur-xl xl:block"
      >
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">
          One relationship
        </p>
        <p className="mt-3 max-w-[220px] font-heading text-lg font-medium leading-snug text-white">
          Five specialised capabilities. One delivery standard. One contract.
        </p>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToId("#group")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2 text-gray-500 transition-colors duration-300 hover:text-gold"
        aria-label="Scroll down"
      >
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          className="block"
        >
          <ArrowDown className="h-5 w-5" />
        </motion.span>
      </motion.button>
    </section>
  );
}
